import {
  createDocument, schemaVersion as profileSchemaVersion,
  samePolicyScope, validateBrowserViewDraft, validatePolicyDraft
} from "./document.js";

export const applicationSchemaVersion = 2;
const masterProfileId = "profile-master";

export function createApplicationDocument(profileDocument = createDocument(), now = new Date().toISOString()) {
  assertValidProfileDocument(profileDocument);
  return {
    schemaVersion: applicationSchemaVersion,
    activeProfileId: masterProfileId,
    profiles: [{
      id: masterProfileId,
      name: "Master",
      createdAt: now,
      updatedAt: now,
      data: structuredClone(profileDocument)
    }]
  };
}

export function migrateApplicationDocument(storedDocument, now = new Date().toISOString()) {
  if (storedDocument?.schemaVersion === applicationSchemaVersion && Array.isArray(storedDocument.profiles)) {
    assertValidApplicationDocument(storedDocument);
    return { document: storedDocument, migrated: false };
  }

  if (storedDocument?.schemaVersion === profileSchemaVersion && Array.isArray(storedDocument.properties)) {
    return { document: createApplicationDocument(storedDocument, now), migrated: true };
  }

  throw new Error(`Unsupported document version: ${storedDocument?.schemaVersion ?? "missing"}`);
}

export function activeProfile(applicationDocument) {
  const profile = applicationDocument.profiles.find((item) => item.id === applicationDocument.activeProfileId);
  if (!profile) throw new Error(`Unknown active profile: ${applicationDocument.activeProfileId}`);
  return profile;
}

export function switchProfile(applicationDocument, profileId) {
  findProfile(applicationDocument, profileId);
  return { ...applicationDocument, activeProfileId: profileId };
}

export function updateActiveProfileData(applicationDocument, data, now = new Date().toISOString()) {
  assertValidProfileDocument(data);
  return {
    ...applicationDocument,
    profiles: applicationDocument.profiles.map((profile) => profile.id === applicationDocument.activeProfileId
      ? { ...profile, updatedAt: now, data: structuredClone(data) }
      : profile)
  };
}

export function createProfile(applicationDocument, draft, createId = defaultCreateId, now = new Date().toISOString()) {
  const name = validateProfileName(applicationDocument, draft.name);
  const source = draft.sourceProfileId ? findProfile(applicationDocument, draft.sourceProfileId) : null;
  const data = source ? copyProfileSections(source.data, draft.sections) : createDocument();
  assertValidProfileDocument(data);
  const profile = {
    id: createId(),
    name,
    createdAt: now,
    updatedAt: now,
    data
  };
  return {
    ...applicationDocument,
    activeProfileId: profile.id,
    profiles: [...applicationDocument.profiles, profile]
  };
}

export function importProfile(applicationDocument, draft, createId = defaultCreateId, now = new Date().toISOString()) {
  const name = validateProfileName(applicationDocument, draft.name);
  assertValidProfileDocument(draft.data);
  const profile = {
    id: createId(),
    name,
    createdAt: now,
    updatedAt: now,
    data: structuredClone(draft.data)
  };
  return {
    ...applicationDocument,
    activeProfileId: profile.id,
    profiles: [...applicationDocument.profiles, profile]
  };
}

export function profileDataWithSectionsReplaced(target, source, sections = {}) {
  return prepareProfileSectionsCopy(target, source, sections).data;
}

export function prepareProfileSectionsCopy(target, source, sections = {}) {
  const data = structuredClone(target);
  const policyChanges = { added: [], retained: [], removed: [] };
  if (sections.configuration) {
    data.properties = structuredClone(source.properties);
    data.assets = structuredClone(source.assets);
    data.assignments = structuredClone(source.assignments);
    data.policies = structuredClone(source.policies ?? []);
    if (sections.keepLocalPolicySettings) {
      const usedPolicyIds = new Set([...data.policies, ...(target.policies ?? [])].map((policy) => policy.id));
      data.policies = data.policies.map((sourcePolicy) => {
        const local = (target.policies ?? []).find((policy) => samePolicyScope(policy.scope, sourcePolicy.scope));
        if (!local) {
          if ((target.policies ?? []).some((policy) => policy.id === sourcePolicy.id)) {
            let suffix = 2;
            while (usedPolicyIds.has(`${sourcePolicy.id}-copy-${suffix}`)) suffix += 1;
            sourcePolicy.id = `${sourcePolicy.id}-copy-${suffix}`;
            usedPolicyIds.add(sourcePolicy.id);
          }
          policyChanges.added.push(sourcePolicy);
          return sourcePolicy;
        }
        policyChanges.retained.push(local);
        return { ...sourcePolicy, id: local.id, must: structuredClone(local.must), should: structuredClone(local.should ?? {}), ...(local.enabled === undefined ? { enabled: true } : { enabled: local.enabled }) };
      });
      for (const local of target.policies ?? []) {
        if (policyChanges.retained.includes(local)) continue;
        if (validatePolicyDraft({ ...data, policies: data.policies }, local)) {
          policyChanges.removed.push(local);
        } else {
          data.policies.push(structuredClone(local));
          policyChanges.retained.push(local);
        }
      }
    } else {
      policyChanges.added = data.policies;
      policyChanges.removed = target.policies ?? [];
    }
  }
  if (sections.allocation) data.allocations = structuredClone(source.allocations ?? []);
  if (sections.browserViews) data.browserViews = structuredClone(source.browserViews ?? []);
  assertValidProfileDocument(data);
  return { data, policyChanges };
}

export function replaceProfileData(applicationDocument, profileId, data, now = new Date().toISOString()) {
  findProfile(applicationDocument, profileId);
  assertValidProfileDocument(data);
  return {
    ...applicationDocument,
    activeProfileId: profileId,
    profiles: applicationDocument.profiles.map((profile) => profile.id === profileId
      ? { ...profile, updatedAt: now, data: structuredClone(data) }
      : profile)
  };
}

export function clearProfileData(applicationDocument, profileId, now = new Date().toISOString()) {
  findProfile(applicationDocument, profileId);
  return {
    ...applicationDocument,
    profiles: applicationDocument.profiles.map((profile) => profile.id === profileId
      ? { ...profile, updatedAt: now, data: createDocument() }
      : profile)
  };
}

export function renameProfile(applicationDocument, profileId, name, now = new Date().toISOString()) {
  findProfile(applicationDocument, profileId);
  const nextName = validateProfileName(applicationDocument, name, profileId);
  return {
    ...applicationDocument,
    profiles: applicationDocument.profiles.map((profile) => profile.id === profileId
      ? { ...profile, name: nextName, updatedAt: now }
      : profile)
  };
}

export function deleteProfile(applicationDocument, profileId) {
  findProfile(applicationDocument, profileId);
  if (applicationDocument.profiles.length === 1) throw new Error("Regulator must have at least one profile.");
  const profiles = applicationDocument.profiles.filter((profile) => profile.id !== profileId);
  return {
    ...applicationDocument,
    activeProfileId: applicationDocument.activeProfileId === profileId ? profiles[0].id : applicationDocument.activeProfileId,
    profiles
  };
}

export function copyProfileSections(source, sections = {}) {
  const data = createDocument();
  if (sections.configuration) {
    data.properties = structuredClone(source.properties);
    data.assets = structuredClone(source.assets);
    data.assignments = structuredClone(source.assignments);
    data.policies = structuredClone(source.policies ?? []);
  }
  if (sections.allocation) data.allocations = structuredClone(source.allocations ?? []);
  if (sections.browserViews) data.browserViews = structuredClone(source.browserViews ?? []);
  return data;
}

export function assertValidApplicationDocument(document) {
  if (document?.schemaVersion !== applicationSchemaVersion) {
    throw new Error(`Unsupported document version: ${document?.schemaVersion ?? "missing"}`);
  }
  if (!Array.isArray(document.profiles) || document.profiles.length === 0) throw new Error("Regulator must have at least one profile.");
  const ids = new Set();
  const names = new Set();
  for (const profile of document.profiles) {
    if (!profile?.id || ids.has(profile.id)) throw new Error("Profile IDs must be present and unique.");
    ids.add(profile.id);
    const name = String(profile.name ?? "").trim();
    const normalizedName = name.toLocaleLowerCase();
    if (!name || names.has(normalizedName)) throw new Error("Profile names must be present and unique.");
    names.add(normalizedName);
    assertValidProfileDocument(profile.data);
  }
  if (!ids.has(document.activeProfileId)) throw new Error(`Unknown active profile: ${document.activeProfileId}`);
}

function assertValidProfileDocument(document) {
  if (document?.schemaVersion !== profileSchemaVersion) throw new Error("Profile data has an unsupported schema version.");
  assertProfileSections(document);
  const assetIds = new Set(document.assets.map((asset) => asset.id));
  const properties = new Map(document.properties.map((property) => [property.id, property]));
  assertValidPropertyVisibility(document.properties, properties);
  assertValidAssignments(document.assignments, assetIds, properties);
  assertValidAllocations(document.allocations ?? [], assetIds);
  assertValidPolicies(document.policies ?? [], document);
  assertValidBrowserViews(document.browserViews ?? [], document);
}

function assertProfileSections(document) {
  for (const key of ["properties", "assets", "assignments", "allocations", "policies", "browserViews"]) {
    if (!Array.isArray(document[key] ?? (key === "allocations" || key === "policies" || key === "browserViews" ? [] : undefined))) {
      throw new Error(`Profile data is missing ${key}.`);
    }
  }
}

function assertValidPropertyVisibility(profileProperties, propertiesById) {
  for (const property of profileProperties) {
    for (const value of property.values) {
      if (value.visibility && !validPropertyValue(propertiesById, value.visibility.propertyId, value.visibility.valueId)) {
        throw new Error(`Property value "${value.name}" has an invalid visibility condition.`);
      }
    }
  }
}

function assertValidAssignments(assignments, assetIds, propertiesById) {
  for (const assignment of assignments) {
    if (!assetIds.has(assignment.assetId) || !validPropertyValue(propertiesById, assignment.propertyId, assignment.valueId)) {
      throw new Error("Asset assignments must reference existing assets, properties, and values.");
    }
  }
}

function assertValidAllocations(allocations, assetIds) {
  for (const allocation of allocations) {
    if (!assetIds.has(allocation.assetId)) throw new Error(`Allocation references unknown asset: ${allocation.assetId}`);
  }
}

function assertValidPolicies(policies, document) {
  for (const [index, policy] of policies.entries()) {
    const otherPolicies = policies.filter((_, policyIndex) => policyIndex !== index);
    const error = validatePolicyDraft({ ...document, policies: otherPolicies }, policy);
    if (error) throw new Error(`Invalid policy: ${error}`);
  }
}

function assertValidBrowserViews(browserViews, document) {
  for (const view of browserViews) {
    const error = validateBrowserViewDraft(document, view);
    if (error) throw new Error(`Invalid Browser view: ${error}`);
  }
}

function validPropertyValue(propertiesById, propertyId, valueId) {
  return propertiesById.get(propertyId)?.values.some((value) => value.id === valueId);
}

function validateProfileName(applicationDocument, value, excludedProfileId) {
  const name = String(value ?? "").trim();
  if (!name) throw new Error("Enter a profile name.");
  if (applicationDocument.profiles.some((profile) => (
    profile.id !== excludedProfileId && profile.name.trim().toLocaleLowerCase() === name.toLocaleLowerCase()
  ))) throw new Error("Profile names must be unique.");
  return name;
}

function findProfile(applicationDocument, profileId) {
  const profile = applicationDocument.profiles.find((item) => item.id === profileId);
  if (!profile) throw new Error(`Unknown profile: ${profileId}`);
  return profile;
}

function defaultCreateId() {
  return `profile-${crypto.randomUUID()}`;
}
